/**
 * Heroes Service
 *
 * Miles and Passport API methods.
 */

import type { CitizenLevelLadder, PassportStampCategory } from '@tonnie/heroes';
import { TonnieClient } from '../client/base';
import { API_ENDPOINTS } from '../client/config';

// ═══════════════════════════════════════════════════════════════════════
// MILES TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface MilesBalance {
  total: number;
  available: number;
  pending: number;
  redeemed: number;
  weeklyEarned: number;
}

export interface MilesTransaction {
  id: string;
  type: 'earned' | 'redeemed' | 'expired';
  amount: number;
  /** Localizable label — render `t(titleKey, titleValues)`. */
  titleKey?: string;
  titleValues?: Record<string, string | number>;
  /** Transitional English fallback. */
  description: string;
  earnedAt: string;
}

export interface MilesHistory {
  balance: MilesBalance;
  transactions: MilesTransaction[];
}

export interface MilesBreakdown {
  breakdown: {
    teleportal: number;
    qultureup: number;
    care: number;
    checkup: number;
    passport: number;
  };
  total: number;
  journeyInstanceId: string | null;
}

// ═══════════════════════════════════════════════════════════════════════
// PASSPORT TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface Passport {
  userId: string;
  level: number;
  title: string;
  badges: Badge[];
  achievements: Achievement[];
  milesHistory: MilesHistory;
  journeyStats: {
    completed: number;
    inProgress: number;
    totalCheckups: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  earnedAt: string;
  category: string;
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  progress: number;
  target: number;
  completed: boolean;
  completedAt?: string;
}

/** Passport timeline event from API */
export interface PassportTimelineEvent {
  id: string;
  type: string;
  /**
   * Localizable label — render `t(titleKey, titleValues)`. `titleValues` may
   * contain `__t:<key>__` placeholders the client resolves via `t()` first.
   * `title` is a transitional English fallback for older payloads.
   */
  titleKey?: string;
  titleValues?: Record<string, string | number>;
  title: string;
  description: string;
  occurredAt: string;
  heroType: string;
  milesEarned?: number;
  isHighlight?: boolean;
  metadata?: Record<string, unknown>;
}

// ═══════════════════════════════════════════════════════════════════════
// STAMP TYPES
// ═══════════════════════════════════════════════════════════════════════

export type StampRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
/** The Passport stamp category vocabulary, owned by `@tonnie/heroes`. */
export type StampCategory = PassportStampCategory;

/** A stamp as `GET /heroes/passport/stamps` (and the company by-talent read) sends it. */
export interface PassportStamp {
  id: string;
  code: string;
  name: string;
  description: string;
  category: StampCategory;
  rarity: StampRarity;
  milesValue: number;
  /** Icon NAME (lucide), resolved by `resolvePassportIcon`. */
  icon: string;
  earned: boolean;
  earnedAt?: string;
  /**
   * 0–100, measured from the stamp's trigger conditions (100 once earned). Sent
   * on the citizen's own read only: a company's read carries none, because the
   * measure reads the citizen's whole record, not the company's journey.
   */
  progress?: number;
}

/**
 * A stamp as `GET /heroes/passport/stamps/:stampId` sends it. The icon NAME
 * travels as `iconUrl` on this route, the field the stamp detail screens read.
 */
export interface StampDetail extends Omit<PassportStamp, 'icon' | 'progress'> {
  iconUrl: string | null;
  /** 0–100, measured from the stamp's trigger conditions (100 once earned). */
  progress: number;
}

export interface StampFilters {
  category?: StampCategory;
  earnedOnly?: boolean;
}

// ═══════════════════════════════════════════════════════════════════════
// CITIZEN BADGE TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface CitizenBadge {
  id: string;
  code: string;
  name: string;
  description: string;
  rarity: StampRarity;
  milesReward: number;
  displayOrder: number;
  icon: string;
  color: string;
  isActive: boolean;
  earned: boolean;
  earnedAt?: string;
  milesAwarded?: number;
  requirements: Record<string, { min: number; type?: string }>;
}

export interface CitizenBadgeProgress {
  badge: CitizenBadge;
  progress: Record<string, { current: number; required: number; met: boolean }>;
  overallProgress: number;
  eligible: boolean;
}

/**
 * Citizen-badges response: the backend returns ALL active badge definitions
 * (earned + locked) plus the earned/total counts — NOT a bare array. The
 * passport page renders the full set (earned highlighted, locked greyed) and
 * the dashboard reads `earnedCount`.
 */
export interface CitizenBadgesResponse {
  badges: CitizenBadge[];
  /** `null` when withheld — never 0, which reads as "earned none". (W3.3) */
  earnedCount: number | null;
  totalCount: number | null;
  /** False when the citizen has revoked `passport_stamps` for this company. */
  entitled?: boolean;
}

// ═══════════════════════════════════════════════════════════════════════
// JOURNEY MILES TYPES
// ═══════════════════════════════════════════════════════════════════════

/**
 * Mirrors the platform-api `PassportService.getJourneyMiles` response. NOTE:
 * the API returns `balance` (the cumulative SUM of journey_miles), NOT
 * `totalMiles` — the previous `{ totalMiles, recentTransactions }` shape was a
 * type lie that made every consumer read `undefined` (the citizen surfaces
 * masked it with a `milesBalance.total` fallback; the business Passport triad
 * had none, so levels read 0 → "1/5"). This now matches the real payload.
 */
export interface JourneyMilesResponse {
  /** `null` when withheld — never 0, which reads as "earned nothing". (W3.3) */
  balance: number | null;
  /**
   * The citizen's level on `balance`, computed by the server — draw it, never
   * rebuild it from the balance. `null` exactly when `balance` is (withheld).
   */
  level: CitizenLevelLadder | null;
  /** False when the citizen has revoked `passport_stamps` for this company. */
  entitled?: boolean;
  transactions: Array<{
    id: string;
    amount: number;
    source?: string;
    sourceType?: string;
    sourceId?: string;
    /** Localizable label — render `t(titleKey, titleValues)`. */
    titleKey?: string;
    titleValues?: Record<string, string | number>;
    /** Transitional English fallback. */
    description?: string;
    earnedAt?: string;
    expiresAt?: string;
    isExpired?: boolean;
  }>;
  pagination: { total: number; limit: number; offset: number };
}

// ═══════════════════════════════════════════════════════════════════════
// EXPORT / SHARE TYPES
// ═══════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

export class HeroesService {
  constructor(private readonly client: TonnieClient) {}

  // Miles Methods
  /**
   * Get current Miles balance
   */
  async getMilesBalance(journeyInstanceId?: string): Promise<MilesBalance> {
    const params = journeyInstanceId ? { journeyInstanceId } : undefined;
    return this.client.get<MilesBalance>(API_ENDPOINTS.HEROES.MILES, { params });
  }

  /**
   * Get Miles transaction history
   */
  async getMilesHistory(params?: { limit?: number; offset?: number }): Promise<MilesHistory> {
    return this.client.get<MilesHistory>(`${API_ENDPOINTS.HEROES.MILES}/history`, { params });
  }

  /**
   * Get Miles breakdown by source
   */
  async getMilesBreakdown(journeyInstanceId?: string): Promise<MilesBreakdown> {
    const params = journeyInstanceId ? { journeyInstanceId } : undefined;
    return this.client.get<MilesBreakdown>(`${API_ENDPOINTS.HEROES.MILES}/breakdown`, { params });
  }

  // Passport Methods
  /**
   * Get user passport
   */
  async getPassport(): Promise<Passport> {
    return this.client.get<Passport>(API_ENDPOINTS.HEROES.PASSPORT);
  }

  /**
   * Get all badges
   */
  async getBadges(): Promise<Badge[]> {
    return this.client.get<Badge[]>(`${API_ENDPOINTS.HEROES.PASSPORT}/badges`);
  }

  /**
   * Get all achievements
   */
  async getAchievements(): Promise<Achievement[]> {
    return this.client.get<Achievement[]>(`${API_ENDPOINTS.HEROES.PASSPORT}/achievements`);
  }

  /**
   * Get user's passport timeline (journey activity feed)
   */
  async getTimeline(limit = 20): Promise<PassportTimelineEvent[]> {
    return this.client.get<PassportTimelineEvent[]>(API_ENDPOINTS.HEROES.PASSPORT_TIMELINE, {
      params: { limit },
    });
  }

  // Stamp Methods

  async getStamps(filters?: StampFilters): Promise<PassportStamp[]> {
    const response = await this.client.get<PassportStamp[] | { stamps: PassportStamp[] }>(
      API_ENDPOINTS.HEROES.PASSPORT_STAMPS,
      {
        params: filters as unknown as Record<string, string | number | boolean | undefined>,
      }
    );
    // API returns { stamps: [...], total, earnedCount } — unwrap to array
    if (Array.isArray(response)) return response;
    if (response && 'stamps' in response && Array.isArray(response.stamps)) return response.stamps;
    return [];
  }

  async getStampDetail(stampId: string): Promise<StampDetail> {
    return this.client.get<StampDetail>(API_ENDPOINTS.HEROES.PASSPORT_STAMP(stampId));
  }

  // Citizen Badge Methods

  async getCitizenBadges(): Promise<CitizenBadgesResponse> {
    return this.client.get<CitizenBadgesResponse>(API_ENDPOINTS.HEROES.PASSPORT_CITIZEN_BADGES);
  }

  async getCitizenBadgeProgress(): Promise<CitizenBadgeProgress[]> {
    return this.client.get<CitizenBadgeProgress[]>(
      API_ENDPOINTS.HEROES.PASSPORT_CITIZEN_BADGES_PROGRESS
    );
  }

  // Journey Miles

  async getJourneyMiles(params?: {
    limit?: number;
    offset?: number;
  }): Promise<JourneyMilesResponse> {
    return this.client.get<JourneyMilesResponse>(API_ENDPOINTS.HEROES.PASSPORT_JOURNEY_MILES, {
      params,
    });
  }

  // ── Business view of a specific talent's passport ──────────────────────────
  // Same response shapes as the citizen self-view above, so the Evidence page
  // renders the identical Stamps/Badges/Levels triad. Server-side RolesGuard
  // restricts these to business users.

  /**
   * A company's view of one talent's stamps — WITH the consent verdict. (W3.3)
   *
   * This used to return a bare `PassportStamp[]`, falling back to `[]` on any
   * shape it did not recognise. The server now withholds this collection when
   * the citizen has revoked `passport_stamps` — and an empty array is exactly
   * how "they earned nothing" looks, which is a different and defamatory claim.
   * The verdict therefore travels with the data instead of being flattened out
   * of it.
   */
  async getStampsByTalent(
    talentId: string,
    /** The journey the company is looking at — its drawer's. */
    journeyId: string,
    filters?: StampFilters
  ): Promise<{ entitled: boolean; stamps: PassportStamp[] }> {
    const response = await this.client.get<
      PassportStamp[] | { entitled?: boolean; stamps?: PassportStamp[] }
    >(API_ENDPOINTS.HEROES.PASSPORT_TALENT_STAMPS(talentId), {
      params: { ...filters, journeyId } as Record<string, string | number | boolean | undefined>,
    });
    // A bare array is the legacy shape — no verdict was sent, so it is data.
    if (Array.isArray(response)) return { entitled: true, stamps: response };
    return {
      entitled: response?.entitled !== false,
      stamps: Array.isArray(response?.stamps) ? response.stamps : [],
    };
  }

  async getCitizenBadgesByTalent(
    talentId: string,
    /** The journey the company is looking at — its drawer's. */
    journeyId: string
  ): Promise<CitizenBadgesResponse> {
    return this.client.get<CitizenBadgesResponse>(
      API_ENDPOINTS.HEROES.PASSPORT_TALENT_CITIZEN_BADGES(talentId),
      { params: { journeyId } }
    );
  }

  async getJourneyMilesByTalent(
    talentId: string,
    /** The journey the company is looking at — its drawer's. */
    journeyId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<JourneyMilesResponse> {
    return this.client.get<JourneyMilesResponse>(
      API_ENDPOINTS.HEROES.PASSPORT_TALENT_JOURNEY_MILES(talentId),
      { params: { ...params, journeyId } }
    );
  }

  // Export & Share
}

export function createHeroesService(client: TonnieClient): HeroesService {
  return new HeroesService(client);
}
